// >>> REV-1's APPROVAL DESK. HER THREE TAPS, AND NOTHING ELSE IN HER FACE. <<<
//
// HER SPEC: *"three-tap desk, first names default, provenance behind the toggle, DM privacy law."*
// And over all of it: **her approvals are the gate before any display renders** -- the home carousel,
// the membership page's three review slots and the desktop review rail are all waiting on this
// screen.
//
// THE THREE TAPS ARE `DESK_ACTIONS`, THE ONE ROSTER THE SERVER RESOLVES FROM (R28/R29/R41): this
// component posts an ACTION KEY and never a state, so nothing she can tap here could publish a
// private conversation even if this file were wrong.

import { useCallback, useEffect, useState } from "react";
import { DESK_ACTIONS } from "../reviews.js";

type DeskRow = {
  id: string;
  name: string;
  body: string;
  original: string;
  state: string;
  source: string;
  private: boolean;
  showable: boolean;
};

type Counts = Record<string, number>;

export function OwnerReviews() {
  // >>> FOUR STATES, NEVER TWO (R18/R65). <<< `undefined` is a read in flight and HOLDS STILL;
  // `null` is a read that ARRIVED AND COULD NOT BE MADE and says so; an empty array is an ANSWER.
  // Collapsing the first two puts "no reviews yet" on a desk whose database refused the query, which
  // on the surface that gates every display would read as her members never having said anything.
  const [rows, setRows] = useState<DeskRow[] | null | undefined>(undefined);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  // HER PROVENANCE TOGGLE. Off by default, which is her word: it never clutters the three-tap flow.
  const [showWhere, setShowWhere] = useState(false);
  const [editing, setEditing] = useState<string>("");
  const [draft, setDraft] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/owner/reviews");
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d?.ok !== true) { setRows(null); setNote(d?.message || "The review desk could not be read just now."); return; }
      setRows(Array.isArray(d.rows) ? d.rows : []);
      setCounts(d.counts || null);
      setNote("");
    } catch {
      setRows(null);
      setNote("The review desk could not be read just now.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = useCallback(async (id: string, action: string, trimmed?: string) => {
    if (busy) return;
    setBusy(id);
    try {
      const r = await fetch("/api/owner/reviews/decide", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(trimmed === undefined ? { id, action } : { id, action, trimmed }),
      });
      const d = await r.json().catch(() => ({}));
      // R39/R47's law: this house refuses with HTTP 200 and `{ ok: false }` on several doors, so the
      // PAYLOAD is read rather than the status code.
      if (!r.ok || d?.ok !== true) { setNote(d?.message || "That did not go through."); return; }
      setEditing("");
      setDraft("");
      await load();
    } catch {
      setNote("That did not go through.");
    } finally {
      setBusy("");
    }
  }, [busy, load]);

  if (rows === undefined) return <p className="owner-note">Reading the desk...</p>;
  if (rows === null) return <p className="owner-note owner-note--warn" role="alert">{note}</p>;

  return (
    <section className="owner-reviews" aria-label="Reviews">
      <p className="owner-note">
        What members said. <strong>Nothing shows anywhere until you approve it.</strong>
        {counts ? ` ${counts.pending || 0} waiting, ${counts.provisional || 0} showing provisionally, ${counts.approved || 0} approved.` : ""}
      </p>

      <label className="owner-reviews__toggle">
        <input type="checkbox" checked={showWhere} onChange={(e) => setShowWhere(e.target.checked)} />
        <span>Show where each one came from</span>
      </label>

      {note ? <p className="owner-note owner-note--warn" role="alert">{note}</p> : null}

      {rows.length === 0 ? (
        <p className="owner-note">Nothing has been harvested yet.</p>
      ) : (
        <ul className="owner-reviews__list">
          {rows.map((row) => (
            <li key={row.id} className="owner-reviews__row" data-review-id={row.id} data-review-state={row.state}>
              <p className="owner-reviews__quote">&ldquo;{row.body}&rdquo;</p>
              <p className="owner-reviews__by">
                {row.name || "no name"}
                {/* >>> A PRIVATE-SURFACE CANDIDATE IS NAMED AS PRIVATE ON HER DESK, ALWAYS, AND NOT
                    BEHIND THE TOGGLE. <<< The toggle hides WHERE it came from; that it came from
                    somewhere private is what makes her tap an informed one, so it is never hidden. */}
                {row.private ? <span className="owner-reviews__private"> · from a private conversation</span> : null}
                {row.state === "provisional" ? <span className="owner-reviews__prov"> · showing provisionally</span> : null}
                {row.state === "approved" ? <span className="owner-reviews__ok"> · approved</span> : null}
                {row.state === "skipped" ? <span className="owner-reviews__skip"> · skipped</span> : null}
                {showWhere ? <span className="owner-reviews__where"> · {row.source}</span> : null}
              </p>

              {editing === row.id ? (
                <div className="owner-reviews__edit">
                  <textarea
                    className="owner-reviews__draft"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    aria-label="Trim this review"
                  />
                  <button type="button" className="button button--primary" disabled={Boolean(busy)}
                    onClick={() => decide(row.id, "trim", draft)}>Approve this wording</button>
                  <button type="button" className="button button--ghost" onClick={() => { setEditing(""); setDraft(""); }}>Cancel</button>
                </div>
              ) : (
                <div className="owner-reviews__acts">
                  {DESK_ACTIONS.map((a: { key: string; label: string }) => (
                    <button
                      key={a.key}
                      type="button"
                      className={a.key === "approve" ? "button button--primary" : "button button--ghost"}
                      data-review-act={a.key}
                      disabled={Boolean(busy)}
                      onClick={() => {
                        if (a.key === "trim") { setEditing(row.id); setDraft(row.body); return; }
                        void decide(row.id, a.key);
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
